import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    Users,
    Briefcase,
    LogOut,
    MessageSquare,
    LayoutDashboard,
    UserCheck,
    ClipboardList,
    Phone,
    X,
    BarChart3,
} from 'lucide-react';
import useAuth from '../../hooks/useAuth';

export default function AdminSidebar({ isOpen, onClose }) {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to logout?')) {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_info');
            navigate('/admin/auth/login');
        }
    };

    const navItems = [
        { path: '/admin/dashboard', name: 'Dashboard', icon: LayoutDashboard },
        ...(isAdmin ? [
            { path: '/admin/referrals', name: 'Referrals', icon: Users },
            { path: '/admin/services', name: 'Services', icon: Briefcase },
            { path: '/admin/contacts', name: 'Contacts', icon: MessageSquare },
        ] : []),
    ];

    const crmItems = [
        { path: '/admin/leads', name: 'Leads', icon: Phone },
        { path: '/admin/referral-sources', name: 'Referral Sources', icon: UserCheck },
        { path: '/admin/tasks', name: 'Tasks', icon: ClipboardList },
    ];

    const adminItems = isAdmin
        ? [
            { path: '/admin/users', name: 'Users', icon: Users },
            { path: '/admin/reports', name: 'Reports', icon: BarChart3 },
        ]
        : [];

    const NavItem = ({ item }) => (
        <NavLink
            key={item.path}
            to={item.path}
            onClick={onClose}
            className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                ${isActive
                    ? 'bg-blue-50 text-blue-700 font-semibold shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }
            `}
        >
            <item.icon className="w-5 h-5" />
            <span>{item.name}</span>
        </NavLink>
    );

    return (
        <>
            {/* Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 lg:hidden z-40 transition-opacity"
                    onClick={onClose}
                />
            )}

            <aside className={`
                w-64 bg-white border-r border-gray-200 h-screen flex flex-col fixed left-0 top-0 z-50 transition-transform duration-300 transform
                ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                        Admin Panel
                    </h2>
                    <button onClick={onClose} className="lg:hidden p-1 text-gray-500 hover:bg-gray-100 rounded-lg">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    {navItems.map((item) => <NavItem key={item.path} item={item} />)}

                    <div className="pt-4 pb-1">
                        <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">CRM</p>
                    </div>
                    {crmItems.map((item) => <NavItem key={item.path} item={item} />)}

                    {adminItems.length > 0 && (
                        <>
                            <div className="pt-4 pb-1">
                                <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
                            </div>
                            {adminItems.map((item) => <NavItem key={item.path} item={item} />)}
                        </>
                    )}
                </nav>

                <div className="p-4 border-t border-gray-100">
                    <button
                        onClick={() => {
                            onClose();
                            handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Logout</span>
                    </button>
                </div>
            </aside>
        </>
    );
}
